import { Reveal, SectionHead } from './UI';
import SplitText from './SplitText';

const PERSONAS = [
  {
    role: 'Plant Heads & COOs',
    title: 'See every site from one screen',
    body: 'Get a single dashboard across all your sites, and know within minutes — not weeks — when something on the floor needs attention. Roll-up reporting for every location you oversee.',
    highlight: 'One view. Every site.',
  },
  {
    role: 'EHS & Compliance Officers',
    title: 'Turn walk-throughs into a record',
    body: "Replace scattered spot-checks with a continuous, time-stamped log you can hand an auditor or inspector on any given day. Evidence that's always current, always searchable.",
    highlight: 'Audit-ready, always.',
  },
  {
    role: 'Operations & Security Teams',
    title: 'Hear about it in seconds, not shifts',
    body: 'Get alerted the moment something happens on the floor, instead of piecing it together from a loss report the next morning. TruVue AI flags what needs your attention so you can act — not react.',
    highlight: 'Alerts in seconds.',
  },
];

export default function WhoFor() {
  return (
    <section id="who" className="section section-cream">
      <style>{`
        #who [class*="eyebrow"]::before,
        #who [class*="eyebrow"]::after {
          content: none !important;
          display: none !important;
        }

        #who .who-container {
          max-width: 1520px;
          margin: 0 auto;
          padding-inline: clamp(16px, 3vw, 24px);
        }

        /* ── Grid ── */
        #who .who-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
          align-items: stretch;
        }

        /* ── Card ── */
        #who .who-card {
          border-radius: 18px;
          border: 1.5px solid #111;
          padding: 24px 22px;
          display: flex;
          flex-direction: column;
          gap: 0;
          min-height: auto;
          transition: transform 0.24s ease, box-shadow 0.24s ease;
          position: relative;
          overflow: hidden;
          background: linear-gradient(180deg, #FBF8FF 0%, #EDE1FB 100%);
        }

        #who .who-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 40px rgba(124, 58, 237, 0.16);
        }

        /* ── Eyebrow label (role prefix) — red ── */
        #who .who-role {
          font-family: var(--font-mono, monospace);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin: 0 0 12px;
          display: block;
          color: #DC2626;
        }

        /* ── Title — purple ── */
        #who .who-title {
          font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
          font-size: clamp(19px, 1.8vw, 24px);
          font-weight: 700;
          line-height: 1.25;
          margin: 0 0 10px;
          color: #6D28D9;
        }

        /* ── Body ── */
        #who .who-body {
          font-size: clamp(13.5px, 1vw, 15px);
          line-height: 1.65;
          margin: 0;
          flex: 1;
          color: #111;
        }

        /* ── Bottom highlight ── */
        #who .who-highlight {
          margin-top: 18px;
          padding-top: 12px;
          font-family: var(--font-mono, monospace);
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.05em;
          border-top: 1px solid rgba(124, 58, 237, 0.18);
          color: #7C3AED;
        }

        /* ── Tablet: 2 col ── */
        @media (max-width: 900px) {
          #who .who-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* ── Mobile: 1 col ── */
        @media (max-width: 560px) {
          #who .who-grid {
            grid-template-columns: 1fr;
          }

          #who .who-card {
            min-height: 320px;
          }
        }
      `}</style>

      <div className="who-container">
        <Reveal>
          <SectionHead
            center
            eyebrow="Who It's For"
            h2="Built for the people who own the outcome"
            p="TruVue AI is designed for the roles that are on the hook when something goes wrong — and the ones that have to prove it didn't."
          />
        </Reveal>

        <div className="who-grid">
          {PERSONAS.map((p, i) => (
            <Reveal key={p.role} delay={i * 100}>
              <div className="who-card">
                <SplitText
                  tag="span"
                  text={p.role}
                  delay={20}
                  duration={0.7}
                  ease="power2.out"
                  splitType="words"
                  from={{ opacity: 0, y: 10 }}
                  to={{ opacity: 1, y: 0 }}
                  threshold={0.1}
                  rootMargin="-40px"
                  textAlign="left"
                  className="who-role"
                />

                <SplitText
                  tag="h3"
                  text={p.title}
                  delay={30}
                  duration={0.8}
                  ease="power3.out"
                  splitType="words"
                  from={{ opacity: 0, y: 20 }}
                  to={{ opacity: 1, y: 0 }}
                  threshold={0.1}
                  rootMargin="-40px"
                  textAlign="left"
                  className="who-title"
                />

                <SplitText
                  tag="p"
                  text={p.body}
                  delay={10}
                  duration={0.7}
                  ease="power2.out"
                  splitType="words"
                  from={{ opacity: 0, y: 15 }}
                  to={{ opacity: 1, y: 0 }}
                  threshold={0.1}
                  rootMargin="-40px"
                  textAlign="left"
                  className="who-body"
                />

                <div className="who-highlight">
                  <SplitText
                    tag="span"
                    text={p.highlight}
                    delay={10}
                    duration={0.7}
                    ease="power2.out"
                    splitType="words"
                    from={{ opacity: 0, y: 10 }}
                    to={{ opacity: 1, y: 0 }}
                    threshold={0.1}
                    rootMargin="-40px"
                    textAlign="left"
                  />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}